import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { useAsyncData } from "@/hooks/useAsyncData";
import { listAll, createItem, removeItem } from "@/lib/adminRepository";

const slugify = (s) =>
  String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Blog categories manager — mirrors MeProperty. These names feed the filter
// chips on the public /resources/blogs page and the post editor's dropdown.
export default function BlogCategories() {
  const { data: rows, loading, error, reload } = useAsyncData(() => listAll("blogCategories"), []);
  const cats = [...(rows || [])].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    if (cats.some((c) => String(c.name || "").toLowerCase() === n.toLowerCase())) {
      toast.error("That category already exists.");
      return;
    }
    setBusy(true);
    try {
      await createItem("blogCategories", { name: n, slug: slugify(n) });
      setName("");
      toast.success("Category added.");
      reload();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[blogCategories] add failed:", err);
      toast.error("Failed to add: " + (err?.message || "error"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cat) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete category "${cat.name}"? Posts keep their text label but lose the filter link.`)) return;
    try {
      await removeItem("blogCategories", cat.id);
      toast.success("Deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-blog-categories">
      <Link to="/admin/blogs" className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-bronze hover:text-bronze_dark mb-5">
        <ArrowLeft size={12} /> Back to blog posts
      </Link>
      <header className="mb-6">
        <div className="eyebrow text-bronze mb-1">Blog</div>
        <h1 className="font-serif text-3xl text-ink">Categories</h1>
        <p className="text-sm text-olive mt-1">These appear as the filter chips on the public blog page and in the post editor's category dropdown.</p>
      </header>

      <form onSubmit={add} className="flex gap-2 mb-6 max-w-md">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New category name, e.g. Market Insights"
          data-testid="blog-category-name"
          className="editorial-input flex-1"
        />
        <button
          type="submit"
          disabled={busy || !name.trim()}
          data-testid="blog-category-add"
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Plus size={14} /> Add
        </button>
      </form>

      {loading && <p className="text-olive">Loading…</p>}
      {error && <p className="text-red-700">Failed to load. <button className="underline" onClick={reload}>Retry</button></p>}

      {!loading && !error && (
        <div className="border border-bronze/20 bg-cream divide-y divide-bronze/10 max-w-md">
          {cats.length === 0 ? (
            <div className="px-5 py-6 text-sm text-olive">No categories yet. Add your first above.</div>
          ) : (
            cats.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-5 py-3.5" data-testid={`blog-category-row-${c.id}`}>
                <div>
                  <div className="text-sm font-medium text-ink">{c.name}</div>
                  <div className="text-[11px] text-olive font-mono">/{c.slug}</div>
                </div>
                <button
                  onClick={() => remove(c)}
                  title="Delete"
                  data-testid={`blog-category-delete-${c.id}`}
                  className="text-red-600 hover:text-red-800 p-2"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
